import { EhrConnectionStatus } from "@api/graphql/types-and-hooks";

/**
 * Pure decision helpers for the EHR client-list import and "Sync to EHR" flows.
 *
 * These encode the branching that decides which dialog/action a click should
 * trigger, kept side-effect free so they can be unit-tested directly. The React
 * handlers stay thin: they compute the action here, then dispatch it.
 *
 * Org-level admin accounts (ehrIntegrationType === ORG_USERNAME_PASSWORD) have no
 * per-provider credentials. The EHR badge shows the org connection as "connected"
 * for the connected state and every active/in-progress state (logging in,
 * reconnecting, importing/exporting) as well as a lingering CONNECTION_ERROR, and
 * a retry restarts the workflow — so all of those are treated as usable (the user
 * can sync/import and retry). Only a genuinely not-set-up or invalid-credentials
 * state routes them to their organization administrator.
 */

// Statuses where an org-admin org's EHR is NOT usable: the connection is
// genuinely not set up, its credentials are invalid, or there is no connection
// record yet. Everything else (Connected, the active/in-progress states, and a
// lingering CONNECTION_ERROR) is treated as usable so a sync/import can proceed.
const ORG_EHR_NOT_USABLE_STATUSES: (EhrConnectionStatus | null | undefined)[] = [
  EhrConnectionStatus.Disconnected,
  EhrConnectionStatus.CredentialsFailed,
  null,
  undefined,
];

/** Whether an org-admin org's EHR connection is usable (a sync/import should proceed). */
export const isOrgEhrUsable = (status: EhrConnectionStatus | null | undefined): boolean =>
  !ORG_EHR_NOT_USABLE_STATUSES.includes(status);

/**
 * User-facing copy explaining why an org-admin org's EHR action is unavailable.
 * Invalid credentials get a distinct message from a not-set-up/disconnected
 * connection, since the remedy differs (restore vs. set up the connection).
 */
export const orgEhrUnusableMessage = (status: EhrConnectionStatus | null | undefined): string =>
  status === EhrConnectionStatus.CredentialsFailed
    ? "Your organization’s EHR credentials are no longer valid. Please contact your organization administrator to restore the connection."
    : "Your organization’s EHR connection is unavailable. Please contact your organization administrator to set it up, then try again.";

// Connected or actively (re)establishing a session. The backend uses the active
// session / stored credentials (which can lag the status — e.g. after connecting
// via the header badge the credentials query isn't invalidated until the next
// CONNECTED transition), so an import/sync proceeds without a login dialog. Applies
// to both provider-level and org-level credential flows.
const CONNECTED_OR_CONNECTING_STATUSES: (EhrConnectionStatus | null | undefined)[] = [
  EhrConnectionStatus.Connected,
  EhrConnectionStatus.LoggingIn,
  EhrConnectionStatus.Reconnecting,
];

/** Whether the EHR is connected or actively (re)connecting — no login dialog needed. */
export const isConnectedOrConnecting = (status: EhrConnectionStatus | null | undefined): boolean =>
  CONNECTED_OR_CONNECTING_STATUSES.includes(status);

/** Managed-onboarding gate inline copy: what continuing would do, plus why
 * it's blocked. Rendered next to the disabled control, never tooltip-only —
 * a disabled element doesn't receive hover/focus events, so a tooltip-only
 * explanation is unreachable by keyboard/screen reader. */
export const EHR_SYNC_GATED_MESSAGE =
  "Continuing will import client data from your EHR. Frontera is still setting up your connection.";

/** Outbound-sync variant of {@link EHR_SYNC_GATED_MESSAGE}: these flows push
 * programming/goals *to* the EHR rather than importing from it, so the inline
 * copy names that action instead. Same managed-onboarding gate. */
export const EHR_SYNC_OUTBOUND_GATED_MESSAGE =
  "Syncing will send your programming to your EHR. Frontera is still setting up your connection.";

/** Inbound programming-refresh variant of {@link EHR_SYNC_GATED_MESSAGE}: the
 * manual "refresh"/"import now" flows pull the latest programming *from* the
 * EHR (not the client roster, and not an outbound push), so the copy names
 * that direction. Same managed-onboarding gate. */
export const EHR_SYNC_REFRESH_GATED_MESSAGE =
  "Refreshing pulls your latest programming from your EHR. Frontera is still setting up your connection.";

/** FDM refuses a manual sync start with status false and this message when an
 * import for the learner is already running or queued (scraper, org-session
 * and CentralReach data warehouse orgs). That import is the one to wait for,
 * so the portal treats it as started. */
export const IMPORT_IN_PROGRESS_MESSAGE = "Import in progress";

/**
 * Why `startManualSyncWorkflow` did not start an import, or null when it
 * started one (or one for this learner is already running). FDM reports a
 * refused start as `status: false` with a message, not as a GraphQL error. A
 * response with no status is a failure too, so the page never waits on it.
 */
export const manualSyncStartFailure = (
  result: { status?: boolean | null; message?: string | null } | null | undefined,
): string | null => {
  const message = result?.message?.trim();
  if (result?.status === true || message === IMPORT_IN_PROGRESS_MESSAGE) return null;
  return message || "Failed to start import";
};

/**
 * Whether a learner's manual-sync status was written since `baseline`, the
 * stamp it carried when the caller started waiting (`undefined`: not loaded
 * yet). Compares server stamps to each other, never to the browser clock.
 */
export const isNewSyncStamp = (stamp: string | null | undefined, baseline: string | null | undefined): boolean =>
  !!stamp && baseline !== undefined && stamp !== baseline;

export type ClientListImportAction = "import" | "open-login" | "gated" | "none";

export interface ClientListImportDecisionInput {
  /** Orgs whose credentials live in the DB (API-key based) — no login dialog. */
  isApiKeyOrg: boolean;
  isEhrIntegrationUser: boolean;
  isOrgUsernamePassword: boolean;
  hasCredentials: boolean;
  ehrConnectionStatus: EhrConnectionStatus | null | undefined;
  /** Managed-onboarding gate: false until Frontera has cleared this tenant's
   * EHR connection, or the integration is graduated. Checked first — an
   * uncleared tenant is blocked regardless of how usable the connection
   * otherwise looks. */
  isEhrSyncCleared: boolean;
}

export const decideClientListImportAction = ({
  isApiKeyOrg,
  isEhrIntegrationUser,
  isOrgUsernamePassword,
  hasCredentials,
  ehrConnectionStatus,
  isEhrSyncCleared,
}: ClientListImportDecisionInput): ClientListImportAction => {
  if (!isEhrSyncCleared) return "gated";
  // API-key orgs, and connected/actively-connecting sessions, import directly —
  // no login dialog (the backend uses the active session / stored creds).
  if (isApiKeyOrg) return "import";
  if (isConnectedOrConnecting(ehrConnectionStatus)) return "import";
  // Saved credentials — backend auto-reconnects even after a login failure.
  if (isEhrIntegrationUser && hasCredentials) return "import";
  // Org-admin: import (and retry) whenever the org connection is usable — including
  // active/in-progress states and a lingering CONNECTION_ERROR; otherwise the org
  // EHR isn't set up, so do nothing (the user contacts their org admin).
  if (isOrgUsernamePassword) {
    return isOrgEhrUsable(ehrConnectionStatus) ? "import" : "none";
  }
  // No saved credentials — first-time per-provider login.
  if (isEhrIntegrationUser) return "open-login";
  return "none";
};

export type EhrExportAlertLike =
  | {
      exportAlert?: { userMessage?: string | null; error?: string | null } | null;
    }
  | null
  | undefined;

const getEhrSyncFailureError = (alerts: EhrExportAlertLike): string | null | undefined => alerts?.exportAlert?.error;

/**
 * Clinician-safe copy for an EHR export sync failure. Prefers `exportAlert.userMessage`
 * from the backend; never surfaces raw `error` text (that stays in Datadog).
 */
export const getEhrSyncFailureUserMessage = (
  alerts: EhrExportAlertLike,
  fallback = "We weren't able to sync your programming to the EHR. Nothing was updated, and your programming is still safe here. Please try again.",
): string => {
  const userMessage = alerts?.exportAlert?.userMessage;
  if (userMessage) return userMessage;
  const error = getEhrSyncFailureError(alerts);
  if (error?.includes("FAILED_CREDS")) {
    return "Incorrect credentials. Please reconnect your EHR to retry.";
  }
  return fallback;
};

export type SyncToEhrAction = "open-sync" | "open-org-contact" | "open-login" | "open-login-pending-sync";

export interface SyncToEhrDecisionInput {
  /** Orgs whose credentials live in the DB (API-key based) — sync directly. */
  isApiKeyOrg: boolean;
  isOrgUsernamePassword: boolean;
  hasCredentials: boolean;
  ehrConnectionStatus: EhrConnectionStatus | null | undefined;
}

export const decideSyncToEhrAction = ({
  isApiKeyOrg,
  isOrgUsernamePassword,
  hasCredentials,
  ehrConnectionStatus,
}: SyncToEhrDecisionInput): SyncToEhrAction => {
  // API-key orgs, and connected/actively-connecting sessions, sync directly.
  if (isApiKeyOrg) return "open-sync";
  if (isConnectedOrConnecting(ehrConnectionStatus)) return "open-sync";
  // Org-admin: sync (and retry) whenever the org connection is usable — including
  // active/in-progress states and a lingering CONNECTION_ERROR. Otherwise the
  // provider has no credentials of their own, so the org EHR is genuinely not set
  // up — direct them to their organization administrator.
  if (isOrgUsernamePassword) {
    return isOrgEhrUsable(ehrConnectionStatus) ? "open-sync" : "open-org-contact";
  }
  // Known-bad credentials — open the login dialog directly.
  if (ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed) return "open-login";
  // Saved credentials — backend auto-reconnects.
  if (hasCredentials) return "open-sync";
  // No credentials — log in, then sync once login succeeds.
  return "open-login-pending-sync";
};

export interface IntegratedButMissingLinkInput {
  hasSelectedLearner: boolean;
  /** Callers pass different predicates here (isEhrScraperUser vs isEhrIntegrationUser);
   * this helper adds the flag override on top of whichever one the caller uses. */
  isIntegratedUser: boolean;
  hasExternalId: boolean;
  allowCreateClientWithEhr: boolean;
}

/**
 * Whether report creation is blocked by a missing EHR link. Orgs opted into manual client
 * creation (`allow-create-client-with-ehr`) are exempt: their clients legitimately have
 * no externalId until the EHR record catches up.
 */
export const computeIntegratedButMissingLink = ({
  hasSelectedLearner,
  isIntegratedUser,
  hasExternalId,
  allowCreateClientWithEhr,
}: IntegratedButMissingLinkInput): boolean =>
  hasSelectedLearner && isIntegratedUser && !hasExternalId && !allowCreateClientWithEhr;
